import React, { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import SEO from '../../components/common/SEO';
import PageSection from '../../components/common/PageSection';
import { getCoursePath, getStaticCourseBySlug, isNumericCourseId } from '../../config/constants/courseData';
import { fetchCourseBySlug } from '../../services/courseService';
import StaticCourseLanding from './components/StaticCourseLanding';
import DynamicCourseDetail from './components/DynamicCourseDetail';

const CourseDetails = () => {
  const { courseSlug } = useParams();
  const [course, setCourse] = useState(undefined);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadCourse = async () => {
      setIsLoading(true);

      const staticCourse = getStaticCourseBySlug(courseSlug);
      if (staticCourse) {
        if (!cancelled) {
          setCourse({ type: 'static', data: staticCourse });
          setIsLoading(false);
        }
        return;
      }

      try {
        const apiCourse = await fetchCourseBySlug(courseSlug);
        if (!cancelled) {
          setCourse({ type: 'dynamic', data: apiCourse });
        }
      } catch {
        if (!cancelled) setCourse(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadCourse();
    return () => {
      cancelled = true;
    };
  }, [courseSlug]);

  if (isLoading) {
    return (
      <>
        <SEO title="Loading Course..." description="Loading course details..." />
        <PageSection className="pt-24 sm:pt-28 md:pt-32 pb-20 min-h-[60vh] flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-designer-primary border-t-transparent rounded-full animate-spin" />
        </PageSection>
      </>
    );
  }

  if (!course) {
    return (
      <>
        <SEO title="Course Not Found | Arvix Creative" description="The course you're looking for doesn't exist." />
        <PageSection className="pt-24 sm:pt-28 md:pt-32 pb-20 min-h-[60vh]">
          <div className="max-w-lg mx-auto text-center">
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-designer-dark text-balance mb-4">Course Not Found</h1>
            <p className="text-gray-600 mb-8">
              The course you&apos;re looking for doesn&apos;t exist or may have been removed.
            </p>
            <Link
              to="/courses"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-designer-primary to-red-600 text-white font-semibold rounded-xl hover:shadow-xl transition-all"
            >
              <FaArrowLeft className="w-4 h-4" />
              Back to Courses
            </Link>
          </div>
        </PageSection>
      </>
    );
  }

  if (course.type === 'static') {
    return <StaticCourseLanding course={course.data} />;
  }

  if (isNumericCourseId(courseSlug) && course.data.slug) {
    return <Navigate to={getCoursePath(course.data)} replace />;
  }

  return <DynamicCourseDetail course={course.data} />;
};

export default CourseDetails;
